import React from 'react';
import { i18n } from 'config/locales';

import { Summary, DateSummary } from 'components/Summary';
import { SUBSTANCE_USE, SUBSTANCE_USE_ALCOHOL_ADDITIONAL } from 'config/formSections/substanceUse';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { alcoholReceivedCounselingAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import ReceivedCounseling from './ReceivedCounseling';

const sectionConfig = {
  key: SUBSTANCE_USE_ALCOHOL_ADDITIONAL.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_ALCOHOL_ADDITIONAL.name,
  storeKey: SUBSTANCE_USE_ALCOHOL_ADDITIONAL.storeKey,
};

export class ReceivedCounselings extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateReceivedTreatment = this.updateReceivedTreatment.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      ReceivedTreatment: this.props.ReceivedTreatment,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateReceivedTreatment = (values) => {
    this.update({
      ReceivedTreatment: values,
      List: values.value === 'Yes' ? this.props.List : [],
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const counselor = o.TreatmentProviderName ? o.TreatmentProviderName.value : '';
    const counselingDates = DateSummary({
      from: o.CounselingDates ? o.CounselingDates.from : {},
      to: o.CounselingDates ? o.CounselingDates.to : {},
    });

    return Summary({
      type: i18n.t('substance.alcohol.receivedCounseling.collection.itemType'),
      index,
      left: counselor,
      right: counselingDates,
      placeholder: i18n.t('substance.alcohol.receivedCounseling.collection.summary'),
    });
  };

  render() {
    const { errors, peopleWhoKnowYou, List } = this.props;
    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, alcoholReceivedCounselingAddressNameFields);
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return (
      <div
        className="section-content received-counselings"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_ADDITIONAL.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.alcohol.additional')}</h1>
        <Branch
          attribute="ReceivedTreatment"
          name="ReceivedTreatment"
          label={i18n.t('substance.alcohol.heading.receivedCounseling')}
          labelSize="h4"
          className="received-treatment"
          {...this.props.ReceivedTreatment}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateReceivedTreatment}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Show when={this.props.ReceivedTreatment.value === 'Yes'}>
          <Accordion
            attribute="List"
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.alcohol.receivedCounseling.collection.description')}
            appendTitle={i18n.t('substance.alcohol.receivedCounseling.collection.appendTitle')}
            appendLabel={i18n.t('substance.alcohol.receivedCounseling.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <ReceivedCounseling
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

ReceivedCounselings.defaultProps = {
  ReceivedTreatment: {},
  List: EmptyListInstance,
  Comments: {},
  SummaryComments: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'alcohol/additional',
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(ReceivedCounselings, sectionConfig);
